import { isDefined } from "@clipboard-health/util-ts";
import { type LatenessConfigResponse } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { ON_TIME_BONUS_GRACE_PERIOD_MINUTES } from "@src/appV2/ClipboardScore/constants";
import { formatTime } from "@src/appV2/redesign/utils/formatTime";
import { parseISO } from "date-fns";
import { useMemo } from "react";

import { wouldAdjustedClockInChangeScore } from "../../components/utils/wouldAdjustedClockInChangeScore";

interface UsePreliminaryScoreAdjustmentMessageParams {
  /** ISO timestamp of the worker's adjusted clock-in time, from a pending missed-punch request. */
  pendingAdjustedClockInIso: string | undefined;
  /** Recorded lateness in minutes (vs. expected arrival). */
  latenessMinutes: number | undefined;
  /** ISO timestamp of the shift's scheduled start. */
  shiftStartIso: string | undefined;
  /** ISO timestamp of the recorded clock-in time. */
  recordedClockInIso: string | undefined;
  /** ISO timestamp of the computed expected arrival time (defaults to shift start when absent). */
  computedArrivalTimeIso: string | undefined;
  /** ISO timestamp of the geofence entry, if recorded. */
  geofenceEntryIso: string | undefined;
  latenessConfig: LatenessConfigResponse | undefined;
  workplaceTimezone: string;
}

/**
 * Builds the contextual message shown alongside the attendance score when a pending
 * missed-punch request would change the worker's score adjustment vs. the recorded
 * clock-in time. Returns undefined when there is no pending request, prerequisites
 * are missing, or the score would not change.
 */
export function usePreliminaryScoreAdjustmentMessage(
  params: UsePreliminaryScoreAdjustmentMessageParams
): string | undefined {
  const {
    pendingAdjustedClockInIso,
    latenessMinutes,
    shiftStartIso,
    recordedClockInIso,
    computedArrivalTimeIso,
    geofenceEntryIso,
    latenessConfig,
    workplaceTimezone,
  } = params;

  return useMemo(() => {
    if (!isDefined(pendingAdjustedClockInIso)) {
      return undefined;
    }

    if (
      !isDefined(latenessMinutes) ||
      !isDefined(shiftStartIso) ||
      !isDefined(recordedClockInIso)
    ) {
      return undefined;
    }

    const expectedArrivalTime = parseISO(computedArrivalTimeIso ?? shiftStartIso);
    const geofenceEntry = isDefined(geofenceEntryIso) ? parseISO(geofenceEntryIso) : undefined;

    const scoreWouldChange = wouldAdjustedClockInChangeScore({
      recordedLatenessMinutes: latenessMinutes,
      adjustedClockInTime: parseISO(pendingAdjustedClockInIso),
      expectedArrivalTime,
      geofenceEntry,
      gracePeriodMinutes: ON_TIME_BONUS_GRACE_PERIOD_MINUTES,
      latenessConfig: latenessConfig ?? [],
    });

    if (!scoreWouldChange) {
      return undefined;
    }

    const formattedRecordedClockInTime = formatTime(
      parseISO(recordedClockInIso),
      workplaceTimezone
    );

    return `This is based on your recorded clock in time of ${formattedRecordedClockInTime}. Your score will be corrected if your request is accepted.`;
  }, [
    pendingAdjustedClockInIso,
    latenessMinutes,
    shiftStartIso,
    recordedClockInIso,
    computedArrivalTimeIso,
    geofenceEntryIso,
    latenessConfig,
    workplaceTimezone,
  ]);
}
